import React, { useEffect, useState } from "react";
import { translateUi } from "./i18n/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { AlertTriangle, Lock, Unlock, X } from "./icons";
import { GbrModalBackdrop } from "./GbrModalBackdrop";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type EmployeeLockState = {
  locked: boolean;
  locked_at?: string | null;
  locked_by_email?: string | null;
  locked_by_name?: string | null;
  lock_reason?: string | null;
};

/** Normalises the SQL flag (0/1, "true", boolean) that arrives on the employee aggregate. */
export function readEmployeeLockFromRecord(record: unknown): EmployeeLockState {
  const row = (record && typeof record === "object" ? record : {}) as Record<string, unknown>;
  const raw = row.locked;
  const locked =
    raw === true ||
    raw === 1 ||
    (typeof raw === "string" && ["1", "true", "yes"].includes(raw.trim().toLowerCase()));
  return {
    locked,
    locked_at: (row.locked_at as string | null) ?? null,
    locked_by_email: (row.locked_by_email as string | null) ?? null,
    locked_by_name: (row.locked_by_name as string | null) ?? null,
    lock_reason: (row.lock_reason as string | null) ?? null,
  };
}

function formatWhen(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

/** Read-only banner shown at the top of a locked profile. */
export function EmployeeProfileLockBanner({
  lock,
  isDarkMode,
}: {
  lock: EmployeeLockState;
  isDarkMode?: boolean;
}) {
  if (!lock.locked) return null;
  const who = lock.locked_by_name || lock.locked_by_email || "";
  const when = formatWhen(lock.locked_at);
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-3 rounded-2xl border px-4 py-3",
        isDarkMode
          ? "border-amber-400/35 bg-amber-950/30 text-amber-100"
          : "border-amber-200 bg-amber-50 text-amber-900",
      )}
    >
      <Lock className={cn("mt-0.5 h-5 w-5 shrink-0", isDarkMode ? "text-amber-300" : "text-amber-600")} />
      <div className="min-w-0 text-sm">
        <p className="font-semibold">
          {translateUi("This profile is locked for evidential purposes and cannot be edited.")}
        </p>
        <p className={cn("mt-0.5", isDarkMode ? "text-amber-200/85" : "text-amber-800")}>
          {who || when
            ? translateUi("Locked by {{who}}{{when}}.", {
                who: who || translateUi("an administrator"),
                when: when ? ` ${translateUi("on")} ${when}` : "",
              })
            : translateUi("An authorised administrator must unlock it before it can be edited again.")}
        </p>
        {lock.lock_reason ? (
          <p className={cn("mt-1", isDarkMode ? "text-amber-200/85" : "text-amber-800")}>
            <span className="font-semibold">{translateUi("Reason")}: </span>
            {lock.lock_reason}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Lock / unlock control. Rendered for administrators and roles granted `employees.lock` — the server
 * enforces the same rule, so this is presentation only.
 */
export function EmployeeProfileLockButton({
  employeeId,
  employeeName,
  lock,
  isDarkMode,
  companyHeaders,
  onLockChange,
}: {
  employeeId: string;
  employeeName: string;
  lock: EmployeeLockState;
  isDarkMode?: boolean;
  /** Tenant + actor headers, matching the rest of the profile's fetches. */
  companyHeaders: Record<string, string>;
  onLockChange: (next: EmployeeLockState) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unlocking = lock.locked;

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const submit = async () => {
    if (busy) return;
    const trimmed = reason.trim();
    if (!unlocking && !trimmed) {
      setError(translateUi("Please record why this profile is being locked."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/employees/${encodeURIComponent(employeeId)}/${unlocking ? "unlock" : "lock"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...companyHeaders },
          body: JSON.stringify({ reason: trimmed }),
          cache: "no-store",
        },
      );
      const payload = (await res.json().catch(() => ({}))) as {
        error?: string;
        lock?: EmployeeLockState;
      };
      if (!res.ok) {
        setError(payload.error || translateUi("The request could not be completed."));
        return;
      }
      onLockChange(payload.lock ?? { locked: !unlocking });
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center px-4 py-2 rounded-xl text-sm font-medium cursor-pointer select-none",
          "transition-all duration-150 hover:shadow-md hover:-translate-y-px",
          "active:translate-y-0 active:scale-[0.97] active:shadow-sm",
          unlocking
            ? isDarkMode
              ? "bg-amber-500/15 border border-amber-400/40 text-amber-200 hover:bg-amber-500/25"
              : "bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
            : isDarkMode
              ? "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800"
              : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50",
        )}
        title={
          unlocking
            ? translateUi("Unlock this profile so it can be edited again")
            : translateUi("Lock this profile from editing for evidential purposes")
        }
      >
        {unlocking ? <Unlock className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
        {unlocking ? translateUi("Unlock Profile") : translateUi("Lock Profile")}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => {
            if (!busy) setOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-lock-title"
            className={cn(
              "relative w-full max-w-lg overflow-hidden rounded-t-3xl border shadow-2xl sm:rounded-3xl",
              isDarkMode
                ? "border-slate-700 bg-slate-900 text-slate-100"
                : "border-slate-200 bg-white text-slate-900",
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <GbrModalBackdrop opacity={isDarkMode ? 0.07 : 0.05} />
            <div className="relative">
              <div className={cn("px-5 pt-5 sm:px-6 sm:pt-6", isDarkMode ? "bg-amber-950/35" : "bg-amber-50/90")}>
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                      isDarkMode ? "bg-amber-900/60 text-amber-200" : "bg-amber-100 text-amber-800",
                    )}
                  >
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 id="employee-lock-title" className="text-lg font-bold leading-snug">
                      {unlocking
                        ? translateUi("Unlock employee profile")
                        : translateUi("Lock employee profile")}
                    </h3>
                    <p className={cn("mt-0.5 text-sm", isDarkMode ? "text-amber-200/85" : "text-amber-800")}>
                      {employeeName}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    disabled={busy}
                    className={cn(
                      "rounded-lg p-2 transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                      isDarkMode ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-white/80",
                    )}
                    aria-label={translateUi("Close")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-4 space-y-2 pb-5">
                  <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                    {unlocking
                      ? translateUi(
                          "Unlocking restores normal editing across every section of this profile, including medicals, competence, incidents and documents. Do this only once the record no longer needs to be preserved as evidence.",
                        )
                      : translateUi(
                          "Locking preserves this record as evidence. Every section becomes read-only for all users — nobody will be able to add, change or delete medicals, competence, training, incidents, cycles, notes or documents until an administrator unlocks it.",
                        )}
                  </p>
                  {!unlocking ? (
                    <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                      {translateUi("The lock, your name and the reason below are written to the audit trail.")}
                    </p>
                  ) : null}
                  {unlocking && lock.lock_reason ? (
                    <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                      <span className="font-semibold">{translateUi("Original reason")}: </span>
                      {lock.lock_reason}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="px-5 pt-4 sm:px-6">
                <label
                  htmlFor="employee-lock-reason"
                  className={cn("block text-xs font-semibold uppercase tracking-wide", isDarkMode ? "text-slate-400" : "text-slate-500")}
                >
                  {unlocking
                    ? translateUi("Reason for unlocking (optional)")
                    : translateUi("Reason for locking (required)")}
                </label>
                <textarea
                  id="employee-lock-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder={
                    unlocking
                      ? translateUi("e.g. Investigation closed, record released for normal use")
                      : translateUi("e.g. SPAD at Signal XY123 on 12 May — record retained for formal investigation")
                  }
                  className={cn(
                    "mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none transition focus:ring-2",
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:ring-amber-500/20"
                      : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-amber-400 focus:ring-amber-200",
                  )}
                />
                {error ? (
                  <p className={cn("mt-2 text-sm font-medium", isDarkMode ? "text-rose-300" : "text-rose-600")}>{error}</p>
                ) : null}
              </div>

              <div className="flex flex-col-reverse gap-2 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className={cn(
                    "min-h-[44px] rounded-xl px-4 text-sm font-semibold disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                    isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
                  )}
                >
                  {translateUi("Cancel")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void submit()}
                  className={cn(
                    "min-h-[44px] rounded-xl px-5 text-sm font-semibold text-white transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed",
                    unlocking
                      ? "bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-600/25"
                      : "bg-amber-600 hover:bg-amber-500 shadow-lg shadow-amber-600/25",
                  )}
                >
                  {busy
                    ? translateUi("Working…")
                    : unlocking
                      ? translateUi("Unlock profile")
                      : translateUi("Lock profile")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
